import {
	useEffect,
	useState,
	useCallback,
	useMemo,
	lazy,
	Suspense,
} from "react";
import { useNavigate } from "react-router-dom";
import { db, auth } from "../firebase";
import {
	collection,
	query,
	where,
	orderBy,
	limit,
	startAfter,
	getDocs,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useUserPreferences } from "../contexts/UserPreferencesContext";
import StickyTitle from "../components/StickyTitle/StickyTitle";
import Loader from "../components/Loader/Loader";
import EmptyState from "../components/EmptyState/EmptyState";
import EntryCard from "../components/HistoryComponents/EntryCard";
import GroupModeMenu from "../components/HistoryComponents/GroupModeMenu";
import UpcomingRecurring from "../components/HistoryComponents/UpcomingRecurring";
import Modal from "../components/Modal/Modal";
import { useTranslation } from "../i18n/i18n";
import useLocale from "../hooks/useLocale";
import {
	WalletCards,
	ArrowRight,
	Utensils,
	Home,
	HeartPulse,
	User,
	Euro,
	Circle,
	CircleAlert,
	FileText,
	CalendarDays,
	PiggyBank,
	CloudDownload,
	Search,
	X,
} from "lucide-react";
import {
	toDate,
	getFullWeekdayWithDayNumber,
	getLocalizedMonthName,
	getYearMonthDay,
} from "../utils/date";
import { formatCurrency } from "../utils/format";
import "../styles/history.css";

// Lazy load EditEntry to avoid the initial assembly cost
const EditEntry = lazy(() =>
	import("../components/HistoryComponents/EditEntry")
);

// Group by modes
const GROUP_MODES = {
	DAY: "day", // Year > Month > Day
	CATEGORY: "category", // Year > Month > Category
};

// Category icons mapping
const categoryIcons = {
	food: <Utensils size={18} />,
	housing: <Home size={18} />,
	health: <HeartPulse size={18} />,
	personal: <User size={18} />,
	savings: <PiggyBank size={18} />,
	income: <Euro size={18} />,
	general: <FileText size={18} />,
	other: <Circle size={18} />,
};

const PAGE_SIZE = 50;

const History = () => {
	const { locale } = useLocale();
	const language = locale.startsWith("fr") ? "fr" : "en";
	const { translate } = useTranslation();
	const navigate = useNavigate();
	const [entries, setEntries] = useState([]);
	const [lastDoc, setLastDoc] = useState(null);
	const [hasMore, setHasMore] = useState(true);
	const [loading, setLoading] = useState(false);
	const [userId, setUserId] = useState(null);
	const [error, setError] = useState(null);
	const [fetchError, setFetchError] = useState(null);

	// View/Edit modal state
	const [selectedEntry, setSelectedEntry] = useState(null);
	const [showModal, setShowModal] = useState(false);
	const [isEditing, setIsEditing] = useState(false);

	// Group mode (by category or by day)
	const { groupMode, setGroupMode } = useUserPreferences();

	// Search state
	const [searchQuery, setSearchQuery] = useState("");
	const [isFetchingAll, setIsFetchingAll] = useState(false);
	const [allFetched, setAllFetched] = useState(false);

	// Force re-render on language change
	const [forceUpdate, setForceUpdate] = useState(0);

	// Get the correct locale string based on language
	const getLocale = useMemo(() => {
		return language === "fr" ? "fr-FR" : "en-GB";
	}, [language]);

	// Reset on language change
	useEffect(() => {
		setForceUpdate(prev => prev + 1);
	}, [language]);

	useEffect(() => {
		const unsubscribe = onAuthStateChanged(auth, user => {
			if (user) {
				setUserId(user.uid);
				setError(null);
			} else {
				setError(translate("alert.userNotLoggedIn"));
				setFetchError(null);
			}
		});
		return () => unsubscribe();
	}, [translate]);

	// Reset data when component mounts, when user changes, or when returning to the page
	useEffect(() => {
		if (userId) {
			setEntries([]);
			setLastDoc(null);
			setHasMore(true);
			setIsInitialLoad(true); // Reset initial load flag
		}
	}, [userId, forceUpdate]);

	useEffect(() => {
		// Add class to html on mount
		document.documentElement.classList.add("history-page-active");

		// Clean up on unmount
		return () => {
			document.documentElement.classList.remove("history-page-active");
		};
	}, []);

	const fetchNextPage = useCallback(async () => {
		if (!userId || loading || !hasMore) return;
		setLoading(true);

		try {
			setFetchError(null);

			let q = query(
				collection(db, "transactions"),
				where("userId", "==", userId),
				orderBy("date", "desc"),
				limit(PAGE_SIZE)
			);

			if (lastDoc) {
				q = query(q, startAfter(lastDoc));
			}

			const snapshot = await getDocs(q);

			const newDocs = snapshot.docs.map(doc => ({
				id: doc.id,
				...doc.data(),
			}));

			// Use functional update to prevent stale closure
			setEntries(prevEntries => {
				const uniqueNewDocs = newDocs.filter(
					doc => !prevEntries.find(entry => entry.id === doc.id)
				);
				return [...prevEntries, ...uniqueNewDocs];
			});

			if (snapshot.docs.length > 0) {
				setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
			}

			setHasMore(
				snapshot.docs.length === PAGE_SIZE && snapshot.docs.length > 0
			);
		} catch (err) {
			const msg =
				err?.code === "unavailable" || !navigator.onLine
					? translate("error.network")
					: translate("error.fetchTransactions");
			setError(null);
			setFetchError(msg);
			console.error(err);
		} finally {
			setLoading(false);
		}
	}, [userId, lastDoc, loading, hasMore, translate]);

	// Fetch ALL entries (used when a search query is active)
	const fetchAllEntries = useCallback(async () => {
		if (!userId || isFetchingAll || allFetched) return;
		setIsFetchingAll(true);

		try {
			setFetchError(null);
			const BATCH = 500;
			let lastBatchDoc = null;
			let keepFetching = true;

			while (keepFetching) {
				let q = query(
					collection(db, "transactions"),
					where("userId", "==", userId),
					orderBy("date", "desc"),
					limit(BATCH)
				);
				if (lastBatchDoc) q = query(q, startAfter(lastBatchDoc));

				const snapshot = await getDocs(q);
				const newDocs = snapshot.docs.map(doc => ({
					id: doc.id,
					...doc.data(),
				}));

				setEntries(prev => {
					const unique = newDocs.filter(d => !prev.find(e => e.id === d.id));
					return [...prev, ...unique];
				});

				if (snapshot.docs.length === BATCH) {
					lastBatchDoc = snapshot.docs[snapshot.docs.length - 1];
				} else {
					keepFetching = false;
				}
			}

			setHasMore(false);
			setAllFetched(true);
		} catch (err) {
			const msg =
				err?.code === "unavailable" || !navigator.onLine
					? translate("error.network")
					: translate("error.fetchTransactions");
			setFetchError(msg);
			console.error(err);
		} finally {
			setIsFetchingAll(false);
		}
	}, [userId, isFetchingAll, allFetched, translate]);

	// Track if this is the initial load
	const [isInitialLoad, setIsInitialLoad] = useState(true);

	useEffect(() => {
		if (userId && isInitialLoad) {
			fetchNextPage();
			setIsInitialLoad(false);
		}
	}, [userId, isInitialLoad, fetchNextPage]);

	// When the user starts searching, fetch everything not yet loaded
	useEffect(() => {
		if (searchQuery.trim() && !allFetched) {
			fetchAllEntries();
		}
	}, [searchQuery, allFetched, fetchAllEntries]);

	// Manual load more handler
	const handleLoadMore = useCallback(() => {
		if (hasMore && !loading) {
			fetchNextPage();
		}
	}, [hasMore, loading, fetchNextPage]);

	// Optimized handler for selecting an entry
	const handleEntrySelect = useCallback(entry => {
		setSelectedEntry(entry);
		setIsEditing(false);
		setShowModal(true);
	}, []);

	// Handler to close the view modal
	const handleCloseModal = useCallback(() => {
		setShowModal(false);

		// Clean up after animation completes
		setTimeout(() => {
			setSelectedEntry(null);
			setIsEditing(false);
		}, 300); // Match the CSS animation duration
	}, []);

	// Handler to toggle edit mode
	const handleEditToggle = useCallback(() => {
		setIsEditing(true);
	}, []);

	// Handler to cancel editing
	const handleCancelEdit = useCallback(() => {
		setIsEditing(false);
	}, []);

	// Handler for when an entry is updated
	const handleEntryUpdated = useCallback(updatedEntry => {
		setSelectedEntry(updatedEntry);
		// Update the entry in the entries list
		setEntries(prevEntries =>
			prevEntries.map(entry =>
				entry.id === updatedEntry.id ? updatedEntry : entry
			)
		);
	}, []);

	// Handler for when an entry is deleted
	const handleEntryDeleted = useCallback(
		entryId => {
			// Remove the entry from the entries list
			setEntries(prevEntries =>
				prevEntries.filter(entry => entry.id !== entryId)
			);
			// Close the modal
			handleCloseModal();
		},
		[handleCloseModal]
	);

	// Filtered entries based on search query
	const filteredEntries = useMemo(() => {
		if (!searchQuery.trim()) return entries;
		const q = searchQuery.toLowerCase().trim();
		return entries.filter(entry => {
			const title = (entry.title || "").toLowerCase();
			const amount = String(entry.amount || "");
			const date = toDate(entry.date);
			const dateStr = date
				? date
						.toLocaleDateString(getLocale, {
							day: "numeric",
							month: "long",
							year: "numeric",
						})
						.toLowerCase()
				: "";
			return title.includes(q) || amount.includes(q) || dateStr.includes(q);
		});
	}, [entries, searchQuery, getLocale]);

	// --- GROUPING BY CATEGORY ---
	const groupByCategory = useMemo(() => {
		return filteredEntries.reduce((acc, entry) => {
			const date = toDate(entry.date);
			if (!date) return acc;

			const year = date.getFullYear();
			// Get localized month name based on current language
			const month = getLocalizedMonthName(date, getLocale);
			const category = (entry.category || "other").toLowerCase();

			acc[year] = acc[year] || {};
			acc[year][month] = acc[year][month] || {};
			acc[year][month][category] = acc[year][month][category] || [];
			acc[year][month][category].push(entry);

			return acc;
		}, {});
	}, [filteredEntries, getLocale]);
	const groupByDay = useMemo(() => {
		return filteredEntries.reduce((acc, entry) => {
			const date = toDate(entry.date);
			if (!date) return acc;

			const year = date.getFullYear();
			// Get localized month name based on current language
			const month = getLocalizedMonthName(date, getLocale);
			const dayKey = getYearMonthDay(date);
			const weekdayDayNumber = getFullWeekdayWithDayNumber(date, getLocale);
			const dayNumber = date.getDate();

			acc[year] = acc[year] || {};
			acc[year][month] = acc[year][month] || {};
			acc[year][month][dayKey] = acc[year][month][dayKey] || {
				entries: [],
				dayNumber,
				weekdayDayNumber,
			};
			acc[year][month][dayKey].entries.push(entry);

			return acc;
		}, {});
	}, [filteredEntries, getLocale]);

	// Calculate totals for category grouping
	const categoryTotals = useMemo(() => {
		return Object.keys(groupByCategory).reduce((acc, year) => {
			const months = groupByCategory[year];
			Object.keys(months).forEach(month => {
				Object.entries(months[month]).forEach(([category, group]) => {
					const totalAmount = group.reduce(
						(sum, entry) => sum + entry.amount,
						0
					);
					acc[year] = acc[year] || {};
					acc[year][month] = acc[year][month] || {};
					acc[year][month][category] = totalAmount;
				});
			});
			return acc;
		}, {});
	}, [groupByCategory]);

	// Calculate totals for day grouping
	const dayTotals = useMemo(() => {
		return Object.keys(groupByDay).reduce((acc, year) => {
			const months = groupByDay[year];
			Object.keys(months).forEach(month => {
				Object.entries(months[month]).forEach(([dayKey, { entries }]) => {
					const totalAmount = entries.reduce(
						(sum, entry) => sum + entry.amount,
						0
					);
					acc[year] = acc[year] || {};
					acc[year][month] = acc[year][month] || {};
					acc[year][month][dayKey] = totalAmount;
				});
			});
			return acc;
		}, {});
	}, [groupByDay]);

	const sortedYears = useMemo(() => {
		return Object.keys(
			groupMode === GROUP_MODES.CATEGORY ? groupByCategory : groupByDay
		).sort((a, b) => b - a);
	}, [groupByCategory, groupByDay, groupMode]);

	// Helper function to sort months in the correct order based on date
	const getSortedMonths = useCallback(
		months => {
			// Create a mapping of month names to their numeric values
			const monthMap = {};
			const now = new Date();
			for (let i = 0; i < 12; i++) {
				now.setMonth(i);
				const monthName = getLocalizedMonthName(now, getLocale);
				monthMap[monthName] = i;
			}

			return Object.keys(months).sort((a, b) => monthMap[b] - monthMap[a]);
		},
		[getLocale]
	);

	// Helper function to sort days
	const getSortedDays = useCallback(days => {
		return Object.keys(days).sort((a, b) => b.localeCompare(a));
	}, []);

	// Handler to navigate to the add transaction page
	const handleNavigateToAdd = useCallback(() => {
		navigate("/add-transaction");
	}, [navigate]);

	// Loading state - initial load
	if (entries.length === 0 && loading) {
		return (
			<div className="content">
				<StickyTitle title={translate("history.title")} />
				<div className="page-content">
					<GroupModeMenu />
					<Loader
						fullscreen={true}
						loadingTextKey="history.loading"
						theme="light"
						translate={translate}
					/>
				</div>
			</div>
		);
	}

	// Auth error state
	if (error && entries.length === 0) {
		return (
			<div className="content">
				<StickyTitle title={translate("history.title")} />
				<div className="page-content">
					<EmptyState
						icon={CircleAlert}
						title={translate("error.title")}
						description={error}
						variant="error"
					/>
				</div>
			</div>
		);
	}

	// Initial state - user needs to load data
	if (entries.length === 0 && !loading && isInitialLoad) {
		return (
			<div className="content">
				<StickyTitle title={translate("history.title")} />
				<div className="page-content">
					<GroupModeMenu />
					<EmptyState
						icon={WalletCards}
						title={translate("history.loadData.title")}
						description={translate("history.loadData.description")}
						actionButton={{
							text: translate("history.loadData.button"),
							onClick: handleLoadMore,
							icon: CloudDownload,
						}}
					/>
				</div>
			</div>
		);
	}

	// Empty state - no data available after trying to load
	if (entries.length === 0 && !loading && !hasMore && isInitialLoad) {
		return (
			<div className="content">
				<StickyTitle title={translate("history.title")} />
				<div className="page-content">
					<EmptyState
						icon={WalletCards}
						title={translate("history.empty.title")}
						description={translate("history.empty.description")}
						actionButton={{
							text: translate("form.add"),
							onClick: handleNavigateToAdd,
							icon: ArrowRight,
						}}
					/>
				</div>
			</div>
		);
	}

	// Main content with data
	return (
		<div className="content">
			{/* View/Edit Entry Modal with lazy loading */}
			<Modal
				isOpen={showModal}
				onClose={handleCloseModal}
				animated={true}
				modalType="edit"
			>
				<div className="edit-modal-content glass-effect">
					<div className="settings-title-container">
						<h1>
							{isEditing ? translate("action.edit") : translate("history.view")}
						</h1>
					</div>
					{selectedEntry && (
						<Suspense fallback={<Loader theme="light" translate={translate} />}>
							<EditEntry
								entry={selectedEntry}
								editable={isEditing}
								onCancel={handleCancelEdit}
								onEditToggle={handleEditToggle}
								onBack={handleCloseModal}
								onEntryUpdated={handleEntryUpdated}
								onEntryDeleted={handleEntryDeleted}
								isRecurring={false}
								isRecurringEdit={false}
							/>
						</Suspense>
					)}
				</div>
			</Modal>

			<StickyTitle title={translate("history.title")} />

			<div className="page-content">
				{/* Toggle button for grouping mode */}
				<GroupModeMenu />

				{/* Search bar */}
				<div className="history-search-container">
					<Search size={16} className="history-search-icon" />
					<input
						type="text"
						className="history-search-input"
						placeholder={translate("history.search.placeholder")}
						value={searchQuery}
						onChange={e => setSearchQuery(e.target.value)}
					/>
					{searchQuery && (
						<button
							className="history-search-clear"
							onClick={() => setSearchQuery("")}
							type="button"
							aria-label="Clear search"
						>
							<X size={14} />
						</button>
					)}
				</div>

				{/* No results message */}
				{searchQuery && filteredEntries.length === 0 && (
					<EmptyState
						icon={Search}
						title={translate("history.search.noResults.title")}
						description={translate("history.search.noResults.description", {
							searchQuery,
						})}
					/>
				)}

				{/* Upcoming recurring transactions preview */}
				{!searchQuery && <UpcomingRecurring />}

				{/* Render by Category */}
				{groupMode === GROUP_MODES.CATEGORY &&
					sortedYears.map(year => {
						const months = groupByCategory[year];
						const sortedMonths = getSortedMonths(months);

						return (
							<div key={year}>
								<h2 className="sticky-header year-header glass-effect">
									{year}
								</h2>
								{sortedMonths.map(month => {
									const categories = months[month];
									return (
										<div key={`${year}-${month}`}>
											<h3 className="sticky-header month-header glass-effect">
												{month}
											</h3>
											{Object.entries(categories).map(([category, group]) => {
												const translatedCategory =
													translate(`category.${category}`) || category;
												return (
													<div
														key={`${year}-${month}-${category}`}
														className={`category-group category-${category}`}
													>
														<h4 className="sticky-header group-header glass-effect">
															<span>
																{categoryIcons[category] ||
																	categoryIcons["other"]}
																{translatedCategory}
															</span>
															<span>
																{formatCurrency(
																	categoryTotals[year][month][category],
																	language
																)}
															</span>
														</h4>
														{group.map(entry => (
															<EntryCard
																key={entry.id}
																entry={entry}
																translate={translate}
																onSelect={() => handleEntrySelect(entry)}
																showDate={true}
																showCategory={false}
															/>
														))}
													</div>
												);
											})}
										</div>
									);
								})}
							</div>
						);
					})}

				{/* Render by Day */}
				{groupMode === GROUP_MODES.DAY &&
					sortedYears.map(year => {
						const months = groupByDay[year];
						const sortedMonths = getSortedMonths(months);

						return (
							<div key={year}>
								<h2 className="sticky-header year-header glass-effect">
									{year}
								</h2>
								{sortedMonths.map(month => {
									const days = months[month];
									const sortedDayKeys = getSortedDays(days);

									return (
										<div key={`${year}-${month}`}>
											<h3 className="sticky-header month-header glass-effect">
												{month}
											</h3>
											{sortedDayKeys.map(dayKey => {
												const { entries: dayEntries, weekdayDayNumber } =
													days[dayKey];

												return (
													<div key={dayKey} className="day-group">
														<h4 className="sticky-header group-header glass-effect">
															<span>
																<CalendarDays size={18} />
																{weekdayDayNumber}
															</span>
															<span>
																{formatCurrency(
																	dayTotals[year][month][dayKey],
																	language
																)}
															</span>
														</h4>
														{dayEntries.map(entry => (
															<EntryCard
																key={entry.id}
																entry={entry}
																translate={translate}
																onSelect={() => handleEntrySelect(entry)}
																showDate={false}
																showCategory={true}
															/>
														))}
													</div>
												);
											})}
										</div>
									);
								})}
							</div>
						);
					})}

				{/* Show fetch error below data if exists */}
				{fetchError && (
					<EmptyState
						icon={CircleAlert}
						title={translate("error.title")}
						description={fetchError}
						variant="error"
					/>
				)}

				{/* Manual load more button — hidden during search */}
				{hasMore && !loading && !searchQuery && (
					<div className="load-more-container">
						<button
							className="ios-button action"
							onClick={handleLoadMore}
							type="button"
						>
							<span>{translate("history.loadMore")}</span>
							<CloudDownload size={18} />
						</button>
					</div>
				)}

				{/* Loading indicator */}
				{(loading || isFetchingAll) && (
					<Loader
						fullscreen={false}
						loadingTextKey="history.loading"
						theme="light"
						translate={translate}
					/>
				)}
			</div>
		</div>
	);
};

export default History;
